import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
// OAuth routes are mounted at /oauth, not /api/oauth
const BACKEND_URL = API_URL.replace(/\/api\/?$/, '');

const SCOPE_LABELS: Record<string, { label: string; description: string }> = {
  'compliance:read': {
    label: 'View compliance data',
    description: 'View your compliance reports, summaries, and check history',
  },
  'compliance:write': {
    label: 'Run compliance checks',
    description: 'Run compliance checks on content and submit feedback on flags',
  },
};

export default function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, token } = useAuthStore();
  const [submitting, setSubmitting] = useState<'grant' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clientId = searchParams.get('client_id');
  const scope = searchParams.get('scope') || 'compliance:read compliance:write';
  const state = searchParams.get('state') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';
  const codeChallenge = searchParams.get('code_challenge') || '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') || 'S256';
  const resource = searchParams.get('resource') || '';

  const scopes = useMemo(() => scope.split(' ').filter(Boolean), [scope]);

  // Fetch client metadata
  const {
    data: client,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['oauth-client', clientId],
    queryFn: async () => {
      const response = await axios.get(`${BACKEND_URL}/oauth/clients/${clientId}`);
      return response.data as {
        client_id: string;
        client_name: string;
        logo_uri?: string;
        client_uri?: string;
        policy_uri?: string;
        tos_uri?: string;
      };
    },
    enabled: !!clientId,
  });

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    const returnUrl = `/oauth/consent${window.location.search}`;
    window.location.href = `/login?redirect=${encodeURIComponent(returnUrl)}`;
    return null;
  }

  async function handleConsent(consent: 'grant' | 'deny') {
    setSubmitting(consent);
    setError(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/oauth/authorize`,
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
          state,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          consent,
          ...(resource && { resource }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          maxRedirects: 0,
          validateStatus: (status) => status < 400,
        }
      );

      // Navigate via server-side 302 redirect endpoint to ensure Teams popup closes properly
      if (response.data?.redirect_uri) {
        window.location.href = `${BACKEND_URL}/oauth/redirect?uri=${encodeURIComponent(response.data.redirect_uri)}`;
      }
    } catch (err: any) {
      setSubmitting(null);
      const message =
        err.response?.data?.error_description ||
        err.response?.data?.error ||
        'Something went wrong. Please try again.';
      setError(message);
    }
  }

  // Missing required params
  if (!clientId || !redirectUri || !codeChallenge) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <span className="text-3xl font-bold text-navy-900">Clarid</span>
          </Link>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Authorization Request</h2>
            <p className="text-gray-600 text-sm">
              This authorization request is missing required parameters. Please return to the
              application and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ocean-600"></div>
      </div>
    );
  }

  // Client not found
  if (fetchError || !client) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <span className="text-3xl font-bold text-navy-900">Clarid</span>
          </Link>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Unknown Application</h2>
            <p className="text-gray-600 text-sm">
              The application requesting access could not be found. It may have been deactivated.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <span className="text-3xl font-bold text-navy-900">Clarid</span>
        </Link>

        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* App identity */}
          <div className="text-center mb-6">
            {client.logo_uri ? (
              <img
                src={client.logo_uri}
                alt={client.client_name}
                className="h-16 w-16 rounded-lg mx-auto mb-3 object-contain"
              />
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-ocean-100 mb-3">
                <svg className="w-8 h-8 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
            )}
            <h2 className="text-xl font-bold text-gray-900">Authorize {client.client_name}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {client.client_name} is requesting access to your Clarid account.
            </p>
          </div>

          {/* Scopes */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              This will allow {client.client_name} to:
            </p>
            <ul className="space-y-3">
              {scopes.map((s) => {
                const info = SCOPE_LABELS[s];
                return (
                  <li key={s} className="flex items-start gap-3">
                    <svg className="h-5 w-5 text-ocean-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{info?.label || s}</p>
                      {info?.description && (
                        <p className="text-xs text-gray-500">{info.description}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => handleConsent('grant')}
              disabled={submitting !== null}
              className="flex w-full justify-center rounded-md border border-transparent bg-ocean-600 py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {submitting === 'grant' ? 'Authorizing...' : 'Allow'}
            </button>
            <button
              onClick={() => handleConsent('deny')}
              disabled={submitting !== null}
              className="flex w-full justify-center rounded-md border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {submitting === 'deny' ? 'Denying...' : 'Deny'}
            </button>
          </div>

          {/* Links */}
          {(client.policy_uri || client.tos_uri || client.client_uri) && (
            <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap justify-center gap-4 text-xs text-gray-400">
              {client.client_uri && (
                <a href={client.client_uri} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
                  Website
                </a>
              )}
              {client.policy_uri && (
                <a href={client.policy_uri} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
                  Privacy Policy
                </a>
              )}
              {client.tos_uri && (
                <a href={client.tos_uri} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
                  Terms of Service
                </a>
              )}
            </div>
          )}

          <p className="mt-4 text-xs text-center text-gray-400">
            You can revoke access at any time from your Clarid settings.
          </p>
        </div>
      </div>
    </div>
  );
}
